import { TFunction } from "i18next";
import { APP_DOMAIN, APP_NAME } from "../../utls/constants";

interface IProfileCustomPageInfos {
  username: string;
  name?: string;
  avatarPath?: string;
  t: TFunction<"translation", undefined>;
}

const useProfileCustomPageInfos = ({ username, name, avatarPath, t }: IProfileCustomPageInfos) => {
  const profileName = name || username;
  const pageTitle = t("profile_page_title", { name: profileName, username });
  const pageDescription = t("profile_page_description", { name: profileName, username });

  document.title = pageTitle;

  let metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
  if (!metaDescription) {
    metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    document.head.appendChild(metaDescription);
  }
  metaDescription.content = pageDescription;

  document.querySelectorAll('link[rel="canonical"]').forEach((link) => link.remove());
  const linkCanonical = document.createElement("link");
  linkCanonical.rel = "canonical";
  linkCanonical.href = `https://www.${APP_DOMAIN}/p/${username}`;
  document.head.appendChild(linkCanonical);

  let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;

  if (!metaRobots) {
    metaRobots = document.createElement("meta");
    metaRobots.name = "robots";
    document.head.appendChild(metaRobots);
  }
  metaRobots.content = "index, follow";

  let scriptJsonLd = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
  if (!scriptJsonLd) {
    scriptJsonLd = document.createElement("script");
    scriptJsonLd.type = "application/ld+json";
    document.head.appendChild(scriptJsonLd);
  }
  const profileUrl = `https://www.${APP_DOMAIN}/p/${username}`;
  const displayName = profileName;
  const imageUrl = avatarPath || `https://www.${APP_DOMAIN}/default-avatar.jpg`;

  scriptJsonLd.textContent = JSON.stringify([
    {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "@id": `${profileUrl}#profile`,
      url: profileUrl,
      name: t("profile_structured_data_title", { name: displayName }),
      description: t("profile_structured_data_description", { name: displayName }),
      isPartOf: {
        "@type": "WebSite",
        name: "Twizz",
        url: `https://www.${APP_DOMAIN}`,
      },
      mainEntity: {
        "@type": "Person",
        "@id": `${profileUrl}#person`,
        name: displayName,
        alternateName: `@${username}`,
        description: t("profile_structured_data_person_description"),
        image: imageUrl,
        url: profileUrl,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: {
            "@id": `https://www.${APP_DOMAIN}`,
            name: "Twizz App",
          },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": `https://www.${APP_DOMAIN}/profiles`,
            name: "Twizz Profils",
          },
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@id": profileUrl,
            name: `${displayName} - @${username}`,
          },
        },
      ],
    },
  ]);

  return () => {
    document.title = APP_NAME;
    if (metaDescription) {
      metaDescription.content = "";
    }
    if (linkCanonical) {
      linkCanonical.remove();
    }
    if (metaRobots) {
      metaRobots.content = "";
    }
    if (scriptJsonLd) {
      scriptJsonLd.remove();
    }
  };
};

export default useProfileCustomPageInfos;
